import { X } from "lucide-react";
import { useT } from "@/lib/i18n";

type SessionTopBarProps = {
  scenarioTitle: string;
  currentStep: number;
  totalSteps: number;
  onClose: () => void;
};

/**
 * Session header — exit X, scenario label + counter, and a thin
 * progress bar. Uses the amber `--scen-*` palette per doctrine §8.3
 * ("chrome amber, content neutral"): the wrapper UI signals "you are
 * inside a scenario," while the inner step content uses the standard
 * Study look so familiar mechanics don't get re-skinned.
 *
 * Mockup: `design/hifi/scenarios.jsx` `ScenarioRunScreen` lines ~334–360.
 */
export function SessionTopBar({ scenarioTitle, currentStep, totalSteps, onClose }: SessionTopBarProps) {
  const t = useT();
  const safeTotal = Math.max(1, totalSteps);
  const pct = Math.min(1, Math.max(0, currentStep / safeTotal));

  return (
    <div className="flex flex-col gap-2 pt-1">
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="exit"
          onClick={onClose}
          className="text-text-2 transition-colors hover:text-text"
        >
          <X className="size-6" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-text">{scenarioTitle}</div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-text-3">
            {t("skills.session_topbar_label")} · {currentStep} / {safeTotal}
          </div>
        </div>
      </div>
      <div
        className="h-[3px] w-full overflow-hidden rounded-full"
        style={{ background: "var(--surface-2)" }}
        role="progressbar"
        aria-valuenow={Math.round(pct * 100)}
      >
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${pct * 100}%`, background: "var(--scen-solid)" }}
        />
      </div>
    </div>
  );
}
